import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import MOCK_PRODUCTS, { getAllCategories } from '../data/products';
import ThoranVisual from '../components/ThoranVisual';

const CATEGORY_META = {
  indian:      { label: 'Indian National Flags', color: '#138808', icon: '🇮🇳' },
  bjp:         { label: 'BJP Flags',             color: '#FF9933', icon: '🟠' },
  congress:    { label: 'Congress Flags',         color: '#0066CC', icon: '🔵' },
  ganesh:      { label: 'Ganesh Festival Flags',  color: '#FF6B35', icon: '🙏' },
  hanuman:     { label: 'Hanuman Flags',          color: '#FF4500', icon: '🕉️' },
  trs:         { label: 'TRS / BRS Flags',        color: '#FF1493', icon: '🟣' },
  tdp:         { label: 'TDP Flags',              color: '#DAA520', icon: '🟡' },
  ramanavami:  { label: 'Sri Ramanavami Flags',   color: '#FFD700', icon: '🪔' },
  buntings:    { label: 'Buntings & Thorans',     color: '#FF6600', icon: '⭐' },
};

const SORT_OPTIONS = [
  { value: 'default',    label: 'Default' },
  { value: 'price-asc',  label: 'Price: Low to High' },
  { value: 'price-desc', label: 'Price: High to Low' },
  { value: 'rating',     label: 'Top Rated' },
];

const ProductListingPage = () => {
  const { category } = useParams();
  const [sort, setSort] = useState('default');
  const [activeCategory, setActiveCategory] = useState(category || 'all');
  const categories = getAllCategories();

  // Sync URL param → state
  useEffect(() => {
    setActiveCategory(category || 'all');
  }, [category]);

  const filtered = activeCategory === 'all'
    ? MOCK_PRODUCTS
    : MOCK_PRODUCTS.filter(p => p.category === activeCategory);

  const sorted = [...filtered].sort((a, b) => {
    if (sort === 'price-asc')  return a.price - b.price;
    if (sort === 'price-desc') return b.price - a.price;
    if (sort === 'rating')     return b.rating - a.rating;
    return 0;
  });

  const meta = CATEGORY_META[activeCategory] || { label: 'All Flags', color: '#ff6600', icon: '🏳️' };
  const discount = (orig, price) => Math.round(((orig - price) / orig) * 100);

  return (
    <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>

      {/* Hero Banner */}
      <div style={{
        background: `linear-gradient(135deg, ${meta.color}22, ${meta.color}44)`,
        borderBottom: `3px solid ${meta.color}`,
        padding: '40px 20px 30px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '8px' }}>{meta.icon}</div>
        <h1 style={{ fontWeight: 700, color: '#222', marginBottom: '4px' }}>{meta.label}</h1>
        <p style={{ color: '#555', marginBottom: 0 }}>{sorted.length} products available</p>
      </div>

      <div className="container py-4">

        {/* Category Pills */}
        <div style={{ overflowX: 'auto', whiteSpace: 'nowrap', marginBottom: '20px' }}>
          <Link
            to="/flags"
            onClick={() => setActiveCategory('all')}
            style={{
              display: 'inline-block', marginRight: '8px', padding: '6px 16px',
              borderRadius: '20px', fontSize: '14px', fontWeight: 600, textDecoration: 'none',
              background: activeCategory === 'all' ? '#ff6600' : '#fff',
              color: activeCategory === 'all' ? '#fff' : '#333',
              border: '1.5px solid #ff6600'
            }}
          >
            All Flags
          </Link>
          {categories.map(cat => {
            const m = CATEGORY_META[cat.id] || {};
            const isActive = activeCategory === cat.id;
            return (
              <Link
                key={cat.id}
                to={`/flags/${cat.id}`}
                onClick={() => setActiveCategory(cat.id)}
                style={{
                  display: 'inline-block', marginRight: '8px', padding: '6px 16px',
                  borderRadius: '20px', fontSize: '14px', fontWeight: 600, textDecoration: 'none',
                  background: isActive ? (m.color || '#ff6600') : '#fff',
                  color: isActive ? '#fff' : '#333',
                  border: `1.5px solid ${m.color || '#ff6600'}`
                }}
              >
                {m.icon} {cat.label} ({cat.count})
              </Link>
            );
          })}
        </div>

        {/* Sort Bar */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <span style={{ color: '#666', fontSize: '14px' }}>{sorted.length} results</span>
          <select
            value={sort}
            onChange={e => setSort(e.target.value)}
            style={{
              padding: '6px 14px', borderRadius: '8px', border: '1px solid #ddd',
              fontSize: '14px', cursor: 'pointer'
            }}
          >
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>

        {/* Product Grid */}
        {sorted.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '80px 20px', color: '#999' }}>
            <div style={{ fontSize: '64px', marginBottom: '16px' }}>🏳️</div>
            <h4>No products found</h4>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
            gap: '20px'
          }}>
            {sorted.map(product => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

const ProductCard = ({ product }) => {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: '#fff',
        borderRadius: '14px',
        overflow: 'hidden',
        boxShadow: hovered ? '0 8px 32px rgba(0,0,0,0.15)' : '0 2px 12px rgba(0,0,0,0.07)',
        transform: hovered ? 'translateY(-4px)' : 'none',
        transition: 'all 0.25s ease',
        position: 'relative'
      }}
    >
      {/* Badge */}
      {product.badge && (
        <div style={{
          position: 'absolute', top: '10px', left: '10px', zIndex: 2,
          background: product.badge === 'Best Seller' ? '#ff6600' :
                      product.badge === 'Premium' ? '#7b2ff7' :
                      product.badge === 'Popular' ? '#138808' : '#e91e63',
          color: '#fff', fontSize: '11px', fontWeight: 700,
          padding: '3px 10px', borderRadius: '10px'
        }}>
          {product.badge}
        </div>
      )}


      {/* Image / Thoran Visual */}
      <Link to={`/flag/${product.id}`} style={{ display: 'block' }}>
        <div style={{ height: '200px', overflow: 'hidden', background: product.bgColor || '#f0f0f0' }}>
          {product.thoranColors ? (
            <ThoranVisual
              thoranColors={product.thoranColors}
              bgColor={product.bgColor}
              height={200}
            />
          ) : (
            <img
              src={product.image}
              alt={product.name}
              style={{
                width: '100%', height: '100%', objectFit: 'cover',
                transition: 'transform 0.4s ease',
                transform: hovered ? 'scale(1.08)' : 'scale(1)'
              }}
            />
          )}
        </div>
      </Link>

      {/* Info */}
      <div style={{ padding: '14px' }}>
        <p style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{product.categoryLabel}</p>
        <Link to={`/flag/${product.id}`} style={{ textDecoration: 'none', color: '#222' }}>
          <h6 style={{ fontWeight: 700, marginBottom: '8px', lineHeight: 1.3 }}>{product.name}</h6>
        </Link>

        {/* Rating */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
          <span style={{ color: '#f59e0b', fontSize: '13px' }}>{'★'.repeat(Math.round(product.rating))}</span>
          <span style={{ fontSize: '12px', color: '#666' }}>({product.reviews})</span>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Link
            to={`/flag/${product.id}`}
            style={{
              flex: 1, textAlign: 'center', padding: '8px',
              background: '#ff6600', color: '#fff', borderRadius: '8px',
              fontWeight: 600, fontSize: '13px', textDecoration: 'none'
            }}
          >
            View Details
          </Link>
          <a
            href={`https://wa.me/919700388626?text=Hi, I'm interested in ${encodeURIComponent(product.name)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: '8px 12px', background: '#25d366', color: '#fff',
              borderRadius: '8px', fontSize: '16px', textDecoration: 'none',
              display: 'flex', alignItems: 'center'
            }}
          >
            <i className="bi bi-whatsapp"></i>
          </a>
        </div>
      </div>
    </div>
  );
};

export default ProductListingPage;
